/**
 * Default site content — extracted verbatim from the original static HTML
 * pages. This is what the public site renders until an admin publishes an
 * updated document to blob storage.
 */
export const defaultSiteContent = {
    version: 1,
    updatedAt: '2026-07-05T00:00:00.000Z',
    hero: {
        eyebrow: 'Contract Executive Support',
        firstName: 'Denise',
        lastName: 'Augusta',
        subtitle: 'Over 25 Years Experience',
        tagline: 'Executive support tailored to your needs—available when you need it, without the commitment of a full-time role.',
    },
    marquee: [
        'Calendar Management',
        'Email & Inbox Management',
        'Document Preparation',
        'Project Coordination',
        'Research & Reporting',
        'Event Planning',
        'Travel Logistics',
        'Onboarding Support',
        'Expense Reconciliation',
        'Executive Support',
    ],
    intro: [
        {
            icon: 'clock',
            title: 'Your Time, Returned',
            body: 'Everything behind the scenes is handled proactively, allowing you to focus fully on the high-impact work only you can do — saving you time, reducing stress and minimizing costly distractions.',
        },
        {
            icon: 'dollar',
            title: 'Convenient Pricing',
            body: 'A straightforward and consistent pricing structure designed for easy management and predictable budgeting.',
        },
        {
            icon: 'globe',
            title: '100% Remote',
            body: 'Seamless integration, immediate impact, and meaningful savings through reduced overhead, eliminated benefits costs, and no onboarding disruption.',
        },
    ],
    servicesNote: "The services listed below represent common areas where clients find the most value — they're not an exhaustive list. Every engagement is a little different, and I'm happy to discuss needs that fall outside these categories. If you don't see exactly what you're looking for, just reach out.",
    services: [
        {
            number: '01',
            title: 'Calendar & Schedule Management',
            description: 'Your time is your most valuable asset. With over 25 years managing complex executive calendars, I keep it protected, organized, and working for you — not against you.',
            includes: [
                'Complex multi-calendar coordination & scheduling',
                'Recurring meeting setup, agendas & minutes',
                'Conflict resolution & time blocking',
                'Travel scheduling, logistics & itineraries',
            ],
        },
        {
            number: '02',
            title: 'Email & Inbox Management',
            description: 'Taming the inbox chaos so nothing falls through the cracks and your attention stays on high-value work. I handle confidential and time-sensitive information with discretion.',
            includes: [
                'Inbox sorting, prioritization & monitoring',
                'Draft responses, follow-ups & formal correspondence',
                'Folder organization, archiving & filing systems',
                'Internal communications & announcements',
            ],
        },
        {
            number: '03',
            title: 'Document & Report Preparation',
            description: 'Polished, professional documents ready to send or present — formatted, proofed, and on time.',
            includes: [
                'Presentations, proposals & executive summaries',
                'Reports, spreadsheets & tracking documents',
                'Invoices, expense reports & reconciliations',
                'Forms & internal communications',
            ],
        },
        {
            number: '04',
            title: 'Research & Data Gathering',
            description: 'Need information fast and organized? I compile, compare, and summarize so you can decide with confidence.',
            includes: [
                'Vendor, product & competitive research',
                'Custom tracking spreadsheet design & maintenance',
                'Data entry, clean-up & reporting',
                'Materials prep for internal & external meetings',
            ],
        },
        {
            number: '05',
            title: 'Project & Task Coordination',
            description: 'Anticipating needs and streamlining workflows keeps your projects moving forward with the right people doing the right things at the right time.',
            includes: [
                'Task list creation, tracking & deadline monitoring',
                'New hire onboarding coordination & itinerary setup',
                'Vendor, team & client follow-up',
                'Event planning, logistics & budget management',
            ],
        },
        {
            number: '06',
            title: 'Personal Life Organization',
            description: 'The same precision I bring to your professional life, applied to everything else competing for your attention. Discretion and confidentiality always guaranteed.',
            includes: [
                'Travel booking, itineraries & logistics',
                'Personal appointment scheduling & coordination',
                'Household vendor coordination & follow-up',
                'Event coordination & planning support',
            ],
        },
    ],
    process: [
        {
            number: '01',
            title: 'Reach Out',
            body: "Contact me and let's schedule a time to talk. Email, Teams, Zoom or via Phone are all great options.",
        },
        {
            number: '02',
            title: 'Discuss Price',
            body: 'After conversation, the best engagement level price is agreed upon and contract signed.',
        },
        {
            number: '03',
            title: 'Hand It Over',
            body: 'After discussing your tools and workflow, I integrate into your world seamlessly.',
        },
        {
            number: '04',
            title: 'Things Get Done',
            body: 'You focus on what matters. I handle the rest — reliably, quietly, completely.',
        },
    ],
    tiers: [
        {
            name: 'Essential',
            subtitle: 'Executive Support Partnership',
            startingAt: '$3,500',
            period: '/month',
            description: '<p><strong>Best for</strong> executives who need consistent, professional support across the day-to-day and the details that keep things moving.</p>',
            featured: true,
        },
        {
            name: 'Enhanced',
            subtitle: 'Executive Support Partnership',
            startingAt: '$5,000',
            period: '/month',
            description: '<p><strong>Best for</strong> executives who want a deeper, more proactive partnership when priorities move fast and you need more of my time each week.</p>',
            featured: false,
        },
    ],
    tierNote: "The tiers above are suggested starting points — not rigid boxes. Every engagement is a conversation. If your needs don't fit neatly into one of these levels, reach out and we'll find an arrangement that actually works for you.",
    addOn: {
        title: 'Personal Administrative Support',
        availability: 'Available with any Support Plan',
        startingAt: '$650',
        period: '/month',
        description: 'For executives who want help managing personal administrative responsibilities alongside their professional priorities.',
        coverage: 'From scheduling appointments and coordinating household services to family logistics, renewals, and personal research — this add-on brings the same calm, organized support to your life outside of work.',
        finePrint: 'Provided remotely and focused on coordination, organization, and research — not in-person errands or physical tasks.',
    },
    expect: [
        {
            title: 'How Engagements Work',
            paragraphs: [
                'I work with a small number of clients at a time, providing a consistent level of support designed to integrate seamlessly into your day-to-day operations.',
                'Each engagement is structured to create clarity, momentum, and continuity—without the need to manage tasks, track hours, or direct the work at every step.',
            ],
        },
        {
            title: 'Getting Started',
            paragraphs: [
                'We begin with a brief consultation to understand your priorities, pace, and where support will have the greatest impact.',
                'From there, we align on the appropriate level of engagement and begin with an initial onboarding period, where I become familiar with your workflows, preferences, and key areas of focus.',
            ],
        },
        {
            title: 'Ongoing Support',
            paragraphs: ['Support is provided on a steady, monthly retainer.', 'This allows for:'],
            bullets: [
                'Consistent availability and responsiveness',
                'Flexibility as priorities shift',
                'Proactive support, rather than reactive task management',
                'Continuity across projects, communication, and planning',
            ],
        },
    ],
    contractBasics: {
        heading: 'Contract Basics',
        paragraphs: [
            'To create stability and meaningful progress, engagements begin with an initial 60-day term.',
            'Following that, services continue on a monthly basis with 30-day advance notice for any changes or conclusion of support.',
        ],
    },
    about: {
        heading: 'The person behind the work',
        paragraphs: [
            "I'm a New Englander at heart – love my sports teams, clam chowdah and all history Boston has to offer. As a young adult I moved to Texas and unexpectedly stayed 12 years – that's where I learned of my preference for heat and sunshine versus scraping ice off my windshield. As an only child raised by a single mom, I was taught to be independent, strong and ready for life. Most of what I know about showing up in the world & for the people in my life, I learned from her—and I'm proud to say it.",
            "When I'm not at my desk, I'm probably cooking, sunk into an adventure novel or watching a good thriller. I'm a devoted cooking-show enthusiast and an eager kitchen experimenter; sometimes the result is dinner, sometimes it goes straight from the oven to the trash. But, I remain vigilant in my desire to master the spatula! Bermuda is my forever spot — I'd visit once a month if time and logistics allowed, and I'm no stranger to (and often welcoming) a road trip.",
            "I'm a lifelong learner, warm by nature, and I bring the same care to my clients that I bring to the people I love most. Calm in the chaos, precise in the details. That's me — at work, and everywhere else.",
        ],
        pullQuote: "These days, my world includes life with my partner, Jay, watching our now-grown children navigate lives of their own (and I admit, a few giggles escape as we watch), and managing a household that could probably qualify for its own reality show—two Huskies, two Siamese cats, and an ongoing diplomatic standoff between them. Most days, I try to run my home the way I run my work: keeping everything moving in sync, making sure everyone has what they need, and somehow making it all look easy.",
    },
    contact: {
        heading: 'Ready to hand off the chaos?',
        quote: "I'm ready when you are.",
        email: 'deniseaugusta1121@gmail.com',
    },
    footer: {
        copyright: '© 2026 Noven Holdings LLC',
    },
};
//# sourceMappingURL=default-content.js.map